import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useFlowObserverPanelStore, FlowObserverPanelEngine } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ExecutionLogEntry, LogLevel, ExecutionStatusData, ExecutionRunState, LogFilterCriteria } from '@flow-studio/flow-observer-core';
import {
  filterExecutionLogEntries,
  getNodeFilterValue,
  getEntityType,
  getEntityTypeLabel,
  getExecutionId,
  getStage,
  getSubStage,
  getDescription,
} from '@flow-studio/flow-observer-core';
import { DetailPane } from '../detailPane/DetailPane.js';
import { useLocalStorage } from '../../hooks/useLocalStorage.js';
import { useResizableColumns, type ColumnDef } from '../../hooks/useResizableColumns.js';
import { LogFilterBar, type LogFilterFieldDef } from '../common/LogFilterBar.js';
import { formatTime } from '../../utils/dates.js';
import { withErrorBoundary } from '../../utils/errorBoundary.js';
import { WebJsonField } from '../shared/WebJsonField.js';

const LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error'];
const ROW_HEIGHT = 20; // px — fixed height enables O(1) virtualizer

// Fixed (non-resizable) leading gutter for the hierarchy-depth indent + per-node color bar — kept
// out of LOG_COLUMNS/columnWidths because its content is derived per-row (entityType/item.id), not
// a data field with its own column, and it must stay a constant width so rows and the header line up.
const HIERARCHY_COL_WIDTH = 24;

// Final column set per the design: Time | Level | Node | Type | Execution ID | Stage | SubStage | Description | Icons | Message.
// Message isn't listed here — it flexes to fill the remaining row width instead of being independently resizable.
const LOG_COLUMNS: ColumnDef[] = [
  { key: 'time', label: 'Time', defaultWidth: 68, minWidth: 50 },
  { key: 'level', label: 'Level', defaultWidth: 44, minWidth: 36 },
  { key: 'node', label: 'Node', defaultWidth: 110, minWidth: 60 },
  { key: 'type', label: 'Type', defaultWidth: 70, minWidth: 50 },
  { key: 'executionId', label: 'Execution ID', defaultWidth: 90, minWidth: 60 },
  { key: 'stage', label: 'Stage', defaultWidth: 80, minWidth: 50 },
  { key: 'subStage', label: 'SubStage', defaultWidth: 70, minWidth: 50 },
  { key: 'description', label: 'Description', defaultWidth: 160, minWidth: 60 },
  { key: 'icons', label: 'Icons', defaultWidth: 64, minWidth: 50 },
];

// The 5 multi-select filters — same field-path constants the columns above read from.
const FILTER_FIELDS: LogFilterFieldDef<ExecutionLogEntry>[] = [
  { key: 'nodes', label: 'Nodes', getValue: getNodeFilterValue },
  { key: 'type', label: 'Type', getValue: getEntityType, getLabel: (value) => getEntityTypeLabel(value) ?? value },
  {
    key: 'executionId',
    label: 'Execution ID',
    getValue: getExecutionId,
    getLabel: (value, allEntries) => {
      const types = new Set(
        allEntries
          .filter((e) => getExecutionId(e) === value)
          .map((e) => getEntityTypeLabel(getEntityType(e)))
          .filter((t): t is string => Boolean(t))
      );
      const short = value.length > 8 ? `${value.slice(0, 8)}…` : value;
      return types.size > 0 ? `${short} (${Array.from(types).join(', ')})` : short;
    },
  },
  { key: 'stage', label: 'Stage', getValue: getStage },
  { key: 'subStage', label: 'Sub Stage', getValue: getSubStage },
];

// Distinct, fixed colors per entity type — the existing hierarchy-indent gutter alone was too
// subtle for Node vs Workflow rows to read apart at a glance (Binoy, 2026-09-24). Applied to the
// Type cell's text/background so it reads at a glance without relying on the indent alone.
const ENTITY_TYPE_COLORS: Record<string, string> = {
  Process: '#a78bfa',       // violet
  ProcessThread: '#38bdf8', // sky blue — "Workflow"
  ProcessElement: '#4ade80', // green — "Node"
};

// Builds the DetailPane "Data" box from the selected log entry's own status/duration/timestamp
// instead of a hardcoded stub — entry.status is only set to 'success'/'failed'/'running'/'cancelled'
// by the producers in useExecutionSignalR.ts; everything else (plain info/error logs) falls back to
// the log level so the badge still reflects something meaningful.
function mapEntryToStatusData(entry: ExecutionLogEntry): ExecutionStatusData {
  const runState: ExecutionRunState =
    entry.status === 'success' ? 'completed'
    : entry.status === 'failed' ? 'failed'
    : entry.status === 'running' ? 'running'
    : entry.status === 'cancelled' ? 'cancelled'
    : entry.level === 'error' ? 'failed'
    : 'completed';
  return {
    runState,
    nodesTotal: 0,
    nodesCompleted: 0,
    nodesFailed: 0,
    elapsedMs: entry.durationMs,
    startedAt: entry.timestamp,
    errorMessage: runState === 'failed' ? entry.message : undefined,
  };
}

// Generate consistent color from numeric ID
function getGroupColorFromId(id?: number): string {
  if (!id) return 'transparent';
  // Use HSL for better color distribution
  const hue = (id * 137.5) % 360; // Golden angle for good color spacing
  const saturation = 45;
  const lightness = 50;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export interface ExecutionLogsTabContentProps extends FlowObserverTabContentProps {
  /** When set, locks the Nodes filter to this node and hides it from the filter bar — the page 04 "Node Execution Logs" tab reuse. */
  nodeKeyFilter?: string;
  /** Distinguishes persisted filter/column/selection state from the primary Execution Logs tab when this component is reused elsewhere. */
  storageKeyPrefix?: string;
}

const ExecutionLogsTabContentInner: React.FC<ExecutionLogsTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
  nodeKeyFilter,
  storageKeyPrefix = 'exec-logs',
}) => {
  const dataBuffer = useFlowObserverPanelStore(
    (s) => {
      const buffer = s.tabRuntimeStates[tabId]?.dataBuffer ?? [];
      return buffer;
    }
  );

  const [activeLevel, setActiveLevel] = useLocalStorage<LogLevel | null>(`${storageKeyPrefix}-filter-level`, null);
  const [autoScroll, setAutoScroll] = useLocalStorage<boolean>(`${storageKeyPrefix}-auto-scroll`, true);
  const [selectedEntry, setSelectedEntry] = useLocalStorage<ExecutionLogEntry | null>(`${storageKeyPrefix}-selected-entry`, null);
  const [splitWidth, setSplitWidth] = useLocalStorage<number>(`${storageKeyPrefix}-split-width`, 20);
  const [pendingFilters, setPendingFilters] = useLocalStorage<Record<string, string[]>>(`${storageKeyPrefix}-pending-filters`, {});
  const [appliedFilters, setAppliedFilters] = useLocalStorage<Record<string, string[]>>(`${storageKeyPrefix}-applied-filters`, {});
  const [isResizing, setIsResizing] = useState(false);
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const [dataModal, setDataModal] = useState<{ title: string; data: unknown } | null>(null);
  const [dataModalView, setDataModalView] = useState<'formatted' | 'json'>('formatted');
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const copyMenuRef = useRef<HTMLDivElement>(null);
  const { widths: columnWidths, startResize } = useResizableColumns(LOG_COLUMNS, `${storageKeyPrefix}-column-widths`);
  const activeFilterFields = useMemo(
    () => (nodeKeyFilter ? FILTER_FIELDS.filter((f) => f.key !== 'nodes') : FILTER_FIELDS),
    [nodeKeyFilter]
  );

  const handleEntryClick = useCallback((entry: ExecutionLogEntry) => {
    setSelectedEntry(entry);
  }, []);

  const appliedCriteria: LogFilterCriteria = useMemo(
    () => ({
      nodeIds: nodeKeyFilter ? [nodeKeyFilter] : appliedFilters.nodes,
      entityTypes: appliedFilters.type,
      executionIds: appliedFilters.executionId,
      stages: appliedFilters.stage,
      subStages: appliedFilters.subStage,
    }),
    [appliedFilters, nodeKeyFilter]
  );

  // visible = levelFilter(executeFilter(buffer)) — the 5 multi-selects are staged until Execute,
  // the existing Level toggle (below) stays immediate, same as it is today.
  const entries = useMemo(() => {
    const executed = filterExecutionLogEntries(dataBuffer as ExecutionLogEntry[], appliedCriteria);
    return executed.filter((e) => !activeLevel || e.level === activeLevel);
  }, [dataBuffer, appliedCriteria, activeLevel]);

  const handlePendingFilterChange = useCallback((key: string, values: string[]) => {
    setPendingFilters((prev) => ({ ...prev, [key]: values }));
  }, []);

  const handleExecuteFilters = useCallback(() => {
    setAppliedFilters(pendingFilters);
  }, [pendingFilters]);

  const handleResetFilters = useCallback(() => {
    setPendingFilters({});
    setAppliedFilters({});
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && entries.length > 0) {
      rowVirtualizer.scrollToIndex(entries.length - 1, { align: 'end' });
    }
  }, [entries.length, autoScroll]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  }, []);

  const showCopySuccess = useCallback((source: string) => {
    setCopyFeedback(`Copied ${source}`);
    setTimeout(() => setCopyFeedback(null), 2000);
  }, []);

  const handleCopyAll = useCallback(() => {
    const allText = entries
      .map((e) => `[${formatTime(e.timestamp)}] ${e.level.toUpperCase()}: ${e.message}`)
      .join('\n');
    navigator.clipboard.writeText(allText).then(() => {
      showCopySuccess('all logs');
    }).catch((err) => {
      console.error('Failed to copy logs:', err);
    });
  }, [entries, showCopySuccess]);

  const handleClearLogs = useCallback(() => {
    useFlowObserverPanelStore.setState((state) => ({
      tabRuntimeStates: {
        ...state.tabRuntimeStates,
        [tabId]: {
          ...state.tabRuntimeStates[tabId],
          dataBuffer: [],
        },
      },
    }));
    FlowObserverPanelEngine.setTabBadge(tabId, undefined);
    window.dispatchEvent(new CustomEvent('flow-observer-clear-logs', { detail: { tabId } }));
    setSelectedEntry(null);
    setActiveLevel(null);
    setDataModal(null);  // Also close data modal when clearing
    setShowCopyMenu(false);  // Close any open menus
  }, [tabId]);

  const handleCopyRawData = useCallback(() => {
    if (entries.length === 0) return;
    const rawData = JSON.stringify(entries, null, 2);
    navigator.clipboard.writeText(rawData).then(() => {
      showCopySuccess('raw data');
    }).catch((err) => {
      console.error('Failed to copy raw data:', err);
    });
  }, [entries, showCopySuccess]);

  const handleCopyBoth = useCallback(() => {
    if (entries.length === 0) return;
    const formattedText = entries
      .map((e) => `[${formatTime(e.timestamp)}] ${e.level.toUpperCase()}: ${e.message}`)
      .join('\n');
    const rawData = JSON.stringify(entries, null, 2);
    const combined = `${formattedText}\n\n--- RAW DATA (JSON) ---\n\n${rawData}`;
    navigator.clipboard.writeText(combined).then(() => {
      showCopySuccess('formatted + raw logs');
    }).catch((err) => {
      console.error('Failed to copy combined data:', err);
    });
  }, [entries, showCopySuccess]);

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!showCopyMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target as Node)) {
        setShowCopyMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCopyMenu]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(15, Math.min(85, newWidth)); // min 15%, max 85%
      setSplitWidth(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setSplitWidth]);

  if (loadState === 'loading') return <div className="fop-loading">Loading…</div>;

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  return (
    <div className="fop-exec-logs">
      <LogFilterBar
        entries={dataBuffer as ExecutionLogEntry[]}
        fields={activeFilterFields}
        pending={pendingFilters}
        onPendingChange={handlePendingFilterChange}
        onExecute={handleExecuteFilters}
        onReset={handleResetFilters}
      />

      {/* Toolbar spans full width above the split */}
      <div className="fop-logs-toolbar">
        <button
          className={`fop-level-btn ${activeLevel === null ? 'fop-level-btn--active' : ''}`}
          onClick={() => setActiveLevel(null)}
        >
          All
        </button>
        {LEVELS.map((lvl) => (
          <button
            key={lvl}
            className={`fop-level-btn ${activeLevel === lvl ? 'fop-level-btn--active' : ''}`}
            onClick={() => setActiveLevel(lvl === activeLevel ? null : lvl)}
          >
            {lvl}
          </button>
        ))}
        <span className="fop-logs-toolbar__spacer" />
        {copyFeedback && (
          <span style={{ color: '#86efac', fontSize: '12px', marginRight: '12px', animation: 'fadeOut 2s forwards' }}>
            ✓ {copyFeedback}
          </span>
        )}
        <div className="fop-logs-count-group">
          <span className="fop-logs-count">{entries.length} events</span>
          <div ref={copyMenuRef} style={{ position: 'relative' }}>
            <button
              className="fop-logs-copy-menu"
              onClick={() => setShowCopyMenu(!showCopyMenu)}
              disabled={entries.length === 0}
              title={entries.length > 0 ? 'Copy options' : 'No entries to copy'}
              aria-label="Copy menu"
              style={{
                marginLeft: '6px',
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid rgba(100, 116, 139, 0.3)',
                background: 'rgba(100, 116, 139, 0.08)',
                color: '#cbd5e1',
                fontSize: '12px',
                fontWeight: '500',
                cursor: entries.length > 0 ? 'pointer' : 'not-allowed',
                opacity: entries.length > 0 ? 1 : 0.5,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onMouseEnter={(e) => {
                if (entries.length > 0) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(100, 116, 139, 0.15)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(100, 116, 139, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (entries.length > 0) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(100, 116, 139, 0.08)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(100, 116, 139, 0.3)';
                }
              }}
            >
              Copy {showCopyMenu ? '▲' : '▼'}
            </button>
            {showCopyMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: '#1e293b',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  borderRadius: '4px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                  minWidth: '160px',
                }}
              >
                <button
                  onClick={() => {
                    handleCopyAll();
                    setShowCopyMenu(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(100, 116, 139, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  Formatted
                </button>
                <button
                  onClick={() => {
                    handleCopyRawData();
                    setShowCopyMenu(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(100, 116, 139, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  JSON
                </button>
                <button
                  onClick={() => {
                    handleCopyBoth();
                    setShowCopyMenu(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(100, 116, 139, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  Both
                </button>
              </div>
            )}
          </div>
          <button
            className="fop-logs-clear-all"
            onClick={handleClearLogs}
            disabled={Boolean(nodeKeyFilter)}
            title={nodeKeyFilter ? 'Clear is disabled here — this view is filtered by node, clearing would wipe the whole live buffer' : 'Clear all logs'}
            aria-label="Clear all logs"
            style={{
              marginLeft: '6px',
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: 'rgba(239, 68, 68, 0.08)',
              color: '#f87171',
              fontSize: '12px',
              fontWeight: '500',
              cursor: nodeKeyFilter ? 'not-allowed' : 'pointer',
              opacity: nodeKeyFilter ? 0.5 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (nodeKeyFilter) return;
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239, 68, 68, 0.15)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239, 68, 68, 0.6)';
            }}
            onMouseLeave={(e) => {
              if (nodeKeyFilter) return;
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239, 68, 68, 0.08)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239, 68, 68, 0.3)';
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Split: list full-width when nothing selected, adjustable width when an entry is selected */}
      <div className="fop-logs-split" ref={splitRef} style={{ display: 'flex' }}>
        <div
          className="fop-logs-split__list"
          style={selectedEntry ? { flex: `0 0 ${splitWidth}%` } : { flex: '1', borderRight: 'none' }}
        >
          {entries.length === 0 ? (
            <div className="fop-empty">No log entries</div>
          ) : (
            <>
              <div className="fop-log-header-row">
                {/* Fixed-width gutter so the hierarchy indent/group-color bar never shifts the columns below out of alignment with these headers. */}
                <div className="fop-log-header-cell" style={{ width: HIERARCHY_COL_WIDTH }} />
                {LOG_COLUMNS.map((col) => (
                  <div
                    key={col.key}
                    className="fop-log-header-cell"
                    style={{ width: columnWidths[col.key] ?? col.defaultWidth }}
                  >
                    <span>{col.label}</span>
                    <span
                      className="fop-log-header-cell__resize"
                      onMouseDown={(e) => startResize(col.key, e)}
                      role="separator"
                      aria-orientation="vertical"
                      title={`Resize ${col.label}`}
                    />
                  </div>
                ))}
                <div className="fop-log-header-cell fop-log-header-cell--flex">
                  <span>Message</span>
                </div>
              </div>
              <div
                className="fop-logs-scroll"
                ref={scrollRef}
                onScroll={handleScroll}
              >
                <div style={{ height: totalSize, width: '100%', position: 'relative' }}>
                  {virtualItems.map((virtualRow) => {
                    const entry = entries[virtualRow.index];
                    const isSelected = selectedEntry?.id === entry.id;
                    const entityType = getEntityType(entry);
                    const itemId = (entry as any)?.rawDetail?.item?.id;
                    const hierarchyPadding = entityType === 'ProcessElement' ? 20 : entityType === 'ProcessThread' ? 10 : 0;
                    const groupColor = getGroupColorFromId(itemId);
                    const nodeLabel = entry.sourceNodeLabel ?? entry.sourceNodeId ?? '';
                    const entityTypeLabel = getEntityTypeLabel(entityType) ?? '';
                    const executionId = getExecutionId(entry) ?? '';
                    const stage = getStage(entry) ?? '';
                    const subStage = getSubStage(entry) ?? '';
                    const description = getDescription(entry) ?? '';
                    return (
                      <button
                        key={entry.id}
                        className={[
                          'fop-log-entry',
                          'fop-log-entry--clickable',
                          isSelected ? 'fop-log-entry--selected' : '',
                        ].filter(Boolean).join(' ')}
                        style={{
                          position: 'absolute',
                          top: virtualRow.start,
                          left: 0,
                          right: '0',
                          height: ROW_HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0,
                          overflow: 'visible',
                          paddingLeft: '8px',
                        }}
                        onClick={() => handleEntryClick(entry)}
                        title="Click to view details"
                        aria-pressed={isSelected}
                      >
                        {/* Same fixed-width gutter as the header — hierarchy depth/group color live inside it, not as a row-wide offset, so every column below stays aligned regardless of entity type. */}
                        <span
                          className="fop-log-cell fop-log-hierarchy"
                          style={{ width: HIERARCHY_COL_WIDTH, display: 'flex', alignItems: 'stretch', padding: 0 }}
                        >
                          <span style={{ marginLeft: hierarchyPadding, width: 4, background: groupColor, flexShrink: 0, borderRadius: 1 }} />
                        </span>
                        <span className="fop-log-cell fop-log-time" style={{ width: columnWidths.time ?? 68 }}>
                          {formatTime(entry.timestamp)}
                        </span>
                        <span
                          className={`fop-log-cell fop-log-level fop-log-level--${entry.level}`}
                          style={{ width: columnWidths.level ?? 44 }}
                        >
                          {entry.level.toUpperCase().slice(0, 3)}
                        </span>
                        <span className="fop-log-cell" style={{ width: columnWidths.node ?? 110 }} title={nodeLabel}>
                          {nodeLabel}
                        </span>
                        <span
                          className="fop-log-cell"
                          style={{
                            width: columnWidths.type ?? 70,
                            color: entityType ? ENTITY_TYPE_COLORS[entityType] : undefined,
                            fontWeight: entityType ? 600 : undefined,
                          }}
                          title={entityTypeLabel}
                        >
                          {entityTypeLabel}
                        </span>
                        <span className="fop-log-cell" style={{ width: columnWidths.executionId ?? 90 }} title={executionId}>
                          {executionId}
                        </span>
                        <span className="fop-log-cell fop-log-stage" style={{ width: columnWidths.stage ?? 80 }} title={stage}>
                          {stage}
                        </span>
                        <span className="fop-log-cell fop-log-substage" style={{ width: columnWidths.subStage ?? 70 }} title={subStage}>
                          {subStage}
                        </span>
                        <span className="fop-log-cell" style={{ width: columnWidths.description ?? 160 }} title={description}>
                          {description}
                        </span>
                        <span className="fop-log-cell fop-log-icons" style={{ width: columnWidths.icons ?? 64 }}>
                          {(() => {
                            const inputData = (entry as any)?.eventInfo?.eventDescription?.metadata?.inputData;
                            const outputData = (entry as any)?.eventInfo?.eventDescription?.metadata?.outputData;
                            const nodeResult = (entry as any)?.eventInfo?.eventDescription?.metadata?.nodeResult;
                            const hasInput = inputData && typeof inputData === 'object' && Object.keys(inputData).length > 0;
                            const hasOutput = (outputData && typeof outputData === 'object' && Object.keys(outputData).length > 0) || nodeResult;
                            const outData = nodeResult || outputData;
                            return (
                              <>
                                {hasInput && (
                                  <span
                                    onClick={(e) => { e.stopPropagation(); setDataModal({ title: `Input — ${entry.sourceNodeLabel || 'Node'}`, data: inputData }); setDataModalView('formatted'); }}
                                    style={{ cursor: 'pointer', fontSize: '9px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#3b82f6', color: 'white', border: 'none' }}
                                    title="View input data"
                                  >IN</span>
                                )}
                                {hasOutput && (
                                  <span
                                    onClick={(e) => { e.stopPropagation(); setDataModal({ title: nodeResult ? `Node Result — ${entry.sourceNodeLabel || 'Node'}` : `Output — ${entry.sourceNodeLabel || 'Node'}`, data: outData }); setDataModalView('formatted'); }}
                                    style={{ cursor: 'pointer', fontSize: '9px', fontWeight: 'bold', padding: '1px 4px', borderRadius: '2px', marginLeft: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#22c55e', color: 'white', border: 'none' }}
                                    title="View result data"
                                  >Result</span>
                                )}
                              </>
                            );
                          })()}
                        </span>
                        <span className="fop-log-message">
                          {entry.message}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Resizable divider */}
        {selectedEntry && (
          <div
            className="fop-logs-split__divider"
            onMouseDown={handleMouseDown}
            title="Drag to resize panels"
            role="separator"
            aria-label="Resize logs and details"
            style={{
              cursor: 'col-resize',
              flex: '0 0 2px',
              background: '#d1d5db',
              userSelect: 'none',
              transition: isResizing ? 'none' : 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isResizing) (e.currentTarget as HTMLElement).style.background = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              if (!isResizing) (e.currentTarget as HTMLElement).style.background = '#d1d5db';
            }}
          />
        )}

        {/* Detail pane — display DetailPane component when an entry is selected */}
        <div className="fop-logs-split__detail" style={selectedEntry ? { flex: `0 0 ${100 - splitWidth}%` } : { display: 'none' }}>
          <DetailPane
            data={selectedEntry ? mapEntryToStatusData(selectedEntry) : undefined}
            eventInfo={selectedEntry?.eventInfo}
            rawDetail={selectedEntry?.rawDetail}
            onClose={() => setSelectedEntry(null)}
          />
        </div>
      </div>

      {/* Data Inspector Modal — opened by clicking ↓IN / ↑OUT icons on log rows */}
      {dataModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDataModal(null)}
        >
          <div
            style={{ background: '#1e293b', border: '1px solid rgba(100,116,139,0.3)', borderRadius: '8px', width: '85vw', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid rgba(100,116,139,0.2)', gap: '6px', flexShrink: 0 }}>
              <span style={{ flex: 1, color: '#e2e8f0', fontSize: '13px', fontWeight: 500 }}>{dataModal.title}</span>
              <button
                onClick={() => setDataModalView('formatted')}
                style={{ padding: '3px 8px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', border: `1px solid ${dataModalView === 'formatted' ? 'rgba(99,102,241,0.6)' : 'rgba(100,116,139,0.3)'}`, background: dataModalView === 'formatted' ? 'rgba(99,102,241,0.15)' : 'transparent', color: dataModalView === 'formatted' ? '#a5b4fc' : '#94a3b8' }}
              >Formatted</button>
              <button
                onClick={() => setDataModalView('json')}
                style={{ padding: '3px 8px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', border: `1px solid ${dataModalView === 'json' ? 'rgba(99,102,241,0.6)' : 'rgba(100,116,139,0.3)'}`, background: dataModalView === 'json' ? 'rgba(99,102,241,0.15)' : 'transparent', color: dataModalView === 'json' ? '#a5b4fc' : '#94a3b8' }}
              >JSON</button>
              <button
                onClick={() => navigator.clipboard.writeText(JSON.stringify(dataModal.data, null, 2)).then(() => showCopySuccess('data')).catch(() => {})}
                style={{ padding: '3px 8px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', border: '1px solid rgba(100,116,139,0.3)', background: 'transparent', color: '#94a3b8' }}
                title="Copy JSON to clipboard"
              >{copyFeedback ? '✓ ' + copyFeedback : 'Copy'}</button>
              <button
                onClick={() => setDataModal(null)}
                style={{ marginLeft: '2px', padding: '2px 7px', borderRadius: '3px', fontSize: '15px', lineHeight: 1, cursor: 'pointer', border: '1px solid rgba(100,116,139,0.3)', background: 'transparent', color: '#94a3b8' }}
                aria-label="Close"
              >×</button>
            </div>
            {/* Modal content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}>
              {dataModalView === 'formatted' ? (
                <WebJsonField value={dataModal.data} />
              ) : (
                <pre style={{ color: '#e2e8f0', fontSize: '12px', lineHeight: '1.5', margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(dataModal.data, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ExecutionLogsTabContent = withErrorBoundary(
  ExecutionLogsTabContentInner,
  <div className="fop-error">Failed to load Execution Logs. Please refresh or try again.</div>
);
